import React, { FC, useContext, useState } from 'react'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import ManageContext from '../contexts/manage'
import { red } from '@mui/material/colors'

import {
  getDateTimeString,
  getProductTitle,
  getDateString,
} from '@kaidam/fc-shared/src/booking/utils'

import Window from '@kaidam/fc-frontend/src/components/dialog/Window'
import DateNavigator  from '@kaidam/fc-frontend/src/components/booking/DateNavigator'
import PublicCalendar from '../components/booking/Calendar'

import {
  BOOKING_STATE_CANCELLED,
} from '@kaidam/fc-shared/src/constants'

const ManageBooking: FC<React.PropsWithChildren<unknown>> = ({}) => {
  const manage = useContext(ManageContext)
  const [ confirmCancelOpen, setConfirmCancelOpen ] = useState(false)
  const [ updateDate, setUpdateDate ] = useState('')

  if(!manage.data) return null

  const {
    booking: {
      data: {
        reference,
        date,
        product,
        state,
      },
      et_data: {
        fullname,
        email,
      }
    },
    org: {
      profile,
    },
  } = manage.data

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            Org:
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            align="left"
            gutterBottom
            variant="h6"
          >
            {profile.name}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            Reference:
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            align="left"
            gutterBottom
            variant="h6"
          >
            {reference}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            State:
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            align="left"
            gutterBottom
            variant="h6"
          >
            {state}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            Date & Time:
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            align="left"
            gutterBottom
            variant="h6"
          >
            {getDateTimeString(new Date(date))}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            Product:
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            align="left"
            gutterBottom
            variant="h6"
          >
            {getProductTitle(product)}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            Heatview Surveyor:
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            align="left"
            gutterBottom
            variant="h6"
          >
            {fullname} ({email})
          </Typography>
        </Grid>
        {state !== BOOKING_STATE_CANCELLED && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              sx={{
                color: (theme) => theme.palette.getContrastText(red[500]),
                backgroundColor: red[500],
                '&:hover': {
                  backgroundColor: red[700],
                },
              }}
              onClick={() => {
                setConfirmCancelOpen(true)
              }}
            >
              Cancel Booking
            </Button>
          </Grid>
        )}
      </Grid>
      {manage.viewingCalendar && (
        <Window
          open
          size="md"
          withCancel
          onCancel={() => manage.setViewingCalendar(null)}
        >
          <Box sx={{
            paddingTop: (theme) => theme.spacing(2),
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <DateNavigator
                value={manage.viewingCalendar.date}
                onChange={(date) => {
                  manage.setViewingCalendar(Object.assign({}, manage.viewingCalendar, {date}))
                }}
              />
            </Box>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <PublicCalendar
                data={manage.calendar}
                length={manage.viewingCalendar.length}
                latest=""
                onSubmit={(date) => {
                  setUpdateDate(date)
                  manage.setViewingCalendar(null)
                }}
              />
            </Box>
          </Box>
        </Window>
      )}
      {updateDate && (
        <Window
          open
          size="md"
          title="Update Booking Date?"
          withCancel
          submitTitle="Confirm"
          onCancel={() => setUpdateDate('')}
          onSubmit={() => {
            manage.update({
              date: updateDate,
            })
            setUpdateDate('')
          }}
        >
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            Are you sure you want to update the date of the booking to:
          </Typography>
          <Typography
            align="left"
            gutterBottom
            color="primary"
            variant="h6"
          >
            {getDateTimeString(new Date(updateDate))}
          </Typography>
        </Window>
      )}
      {confirmCancelOpen && (
        <Window
          open
          size="md"
          title="Cancel Booking?"
          withCancel
          submitTitle="Yes - Cancel the booking"
          cancelTitle="No - Don't Cancel"
          onCancel={() => setConfirmCancelOpen(false)}
          onSubmit={async () => {
            await manage.cancelBooking()
            setConfirmCancelOpen(false)
          }}
        >
          <Typography
            align="left"
            gutterBottom
            variant="body1"
          >
            Are you sure you want to cancel the booking?
          </Typography>
        </Window>
      )}
    </Box>
  )
}

export default ManageBooking